import React, { useState, useEffect } from 'react';
import LogoPolderzicht from '../assets/logo_polderzicht_kantoorlaag.png';
import '../css/BorstweringenPlanning.css';

// Use empty string so fetch uses relative URLs through the proxy
const API_URL = '';

const translations = {
  nl: {
    title: 'Inschrijving Borstweringen Isolatie',
    subtitle: 'Weekend 28 & 29 maart 2026',
    greeting: 'Beste bewoner,',
    intro: 'Komend weekend komen de vakmensen van Robertson weer langs om de borstweringen op de balkons te isoleren (de witte platen in het kozijn).',
    instructions: 'Hieronder kunt u zich inschrijven voor een tijdslot. Wij vragen u om thuis te zijn zodat de monteurs toegang hebben tot uw balkon. Per tijdslot is er ruimte voor een beperkt aantal woningen. Wie het eerst komt, het eerst maalt!',
    waitlistInfo: 'Staat uw gewenste tijdslot vol? Dan kunt u zich op de <strong>wachtlijst</strong> plaatsen. Mocht er een plek vrijkomen, dan wordt u automatisch ingeschreven.',
    nextPlanning: 'Na dit weekend wordt er weer een volgende planning gemaakt.',
    step1: 'Stap 1: Beschikbare tijdsloten - selecteer er een',
    step2: 'Stap 2: Uw gegevens',
    saturday: 'Zaterdag 28 maart',
    sunday: 'Zondag 29 maart',
    alreadyBooked: 'Al ingeschreven',
    full: 'Vol - Wachtlijst mogelijk',
    spotsAvailable: (n, max) => `${n} van ${max} ${n === 1 ? 'plek' : 'plekken'} beschikbaar`,
    onWaitlist: (n) => `${n} op wachtlijst`,
    houseNumber: 'Huisnummer',
    houseNumberPlaceholder: 'Bijv. 42',
    name: 'Naam',
    namePlaceholder: 'Uw volledige naam',
    email: 'E-mailadres',
    emailPlaceholder: 'uw@email.nl',
    phone: 'Telefoonnummer',
    phonePlaceholder: '06-12345678',
    selected: 'Geselecteerd',
    waitlistLabel: '(wachtlijst)',
    submitting: 'Bezig met inschrijven...',
    joinWaitlist: 'Plaats mij op de wachtlijst',
    register: 'Inschrijven',
    yourBookings: 'Uw huidige inschrijvingen',
    waitlist: 'Wachtlijst',
    footer: 'Heeft u vragen? Neem contact op via',
    loading: 'Laden...',
    errorLoading: 'Kan tijdsloten niet laden. Probeer het later opnieuw.',
    errorSelect: 'Selecteer een tijdslot',
    errorGeneral: 'Er is een fout opgetreden. Probeer het opnieuw.',
  },
  en: {
    title: 'Registration Parapet Insulation',
    subtitle: 'Weekend March 28 & 29, 2026',
    greeting: 'Dear resident,',
    intro: 'This weekend, the specialists from Robertson will return to insulate the parapets on the balconies (the white panels in the window frame).',
    instructions: 'Below you can register for a time slot. We ask you to be home so the technicians can access your balcony. Each time slot has limited capacity. First come, first served!',
    waitlistInfo: 'Is your preferred time slot full? You can join the <strong>waiting list</strong>. If a spot becomes available, you will be automatically registered.',
    nextPlanning: 'After this weekend, a new schedule will be made.',
    step1: 'Step 1: Available time slots - select one',
    step2: 'Step 2: Your details',
    saturday: 'Saturday March 28',
    sunday: 'Sunday March 29',
    alreadyBooked: 'Already registered',
    full: 'Full - Waiting list available',
    spotsAvailable: (n, max) => `${n} of ${max} ${n === 1 ? 'spot' : 'spots'} available`,
    onWaitlist: (n) => `${n} on waiting list`,
    houseNumber: 'House number',
    houseNumberPlaceholder: 'E.g. 42',
    name: 'Name',
    namePlaceholder: 'Your full name',
    email: 'Email address',
    emailPlaceholder: 'your@email.com',
    phone: 'Phone number',
    phonePlaceholder: '06-12345678',
    selected: 'Selected',
    waitlistLabel: '(waiting list)',
    submitting: 'Registering...',
    joinWaitlist: 'Join the waiting list',
    register: 'Register',
    yourBookings: 'Your current registrations',
    waitlist: 'Waiting list',
    footer: 'Questions? Contact us at',
    loading: 'Loading...',
    errorLoading: 'Unable to load time slots. Please try again later.',
    errorSelect: 'Please select a time slot',
    errorGeneral: 'An error occurred. Please try again.',
  }
};

const BorstweringenPlanning = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [existingBookings, setExistingBookings] = useState([]);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState(localStorage.getItem('language') || 'nl');
  const [form, setForm] = useState({
    naam: '',
    email: '',
    telefoon: '',
    huisnummer: ''
  });

  const t = translations[lang];

  useEffect(() => {
    localStorage.setItem('language', lang);
  }, [lang]);

  useEffect(() => {
    fetchSlots();
  }, []);

  // When huisnummer changes, check existing bookings
  useEffect(() => {
    if (form.huisnummer && form.huisnummer.length >= 1) {
      checkExisting(form.huisnummer);
    } else {
      setExistingBookings([]);
    }
  }, [form.huisnummer]);

  const fetchSlots = async () => {
    try {
      const res = await fetch(`${API_URL}/api/borstweringen/slots`);
      const data = await res.json();
      setSlots(data);
    } catch (err) {
      setError(t.errorLoading);
    } finally {
      setLoading(false);
    }
  };

  const checkExisting = async (huisnummer) => {
    try {
      const res = await fetch(`${API_URL}/api/borstweringen/check/${huisnummer}`);
      const data = await res.json();
      setExistingBookings(data);
    } catch (err) {
      // Silently fail
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) {
      setError(t.errorSelect);
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API_URL}/api/borstweringen/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, slotId: selectedSlot })
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message);
        return;
      }

      setSuccess(data.message);
      setSelectedSlot(null);
      fetchSlots();
      checkExisting(form.huisnummer);
    } catch (err) {
      setError(t.errorGeneral);
    } finally {
      setSubmitting(false);
    }
  };

  const isAlreadyBooked = (slotId) => {
    return existingBookings.some(b => b.slotId === slotId);
  };

  const getDayLabel = (datum) => {
    return datum === '2026-03-28' ? t.saturday : t.sunday;
  };

  if (loading) {
    return (
      <div className="borstwering-page">
        <div className="borstwering-container">
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="borstwering-page">
      <div className="borstwering-container">
        {/* Language toggle */}
        <div className="lang-toggle">
          <button
            className={`lang-btn ${lang === 'nl' ? 'active' : ''}`}
            onClick={() => setLang('nl')}
          >
            NL
          </button>
          <button
            className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
            onClick={() => setLang('en')}
          >
            EN
          </button>
        </div>

        <div className="borstwering-header">
          <img src={LogoPolderzicht} alt="VVE Polderzicht" className="borstwering-logo" />
          <h1>{t.title}</h1>
          <p className="borstwering-subtitle">{t.subtitle}</p>
        </div>

        <div className="borstwering-info">
          <p>{t.greeting}</p>
          <p>{t.intro}</p>
          <p>{t.instructions}</p>
          <p dangerouslySetInnerHTML={{ __html: t.waitlistInfo }} />
          <p>{t.nextPlanning}</p>
        </div>

        {/* Slot overview */}
        <div className="borstwering-slots">
          <h2>{t.step1}</h2>

          <div className="slots-grid">
            {slots.map(slot => {
              const booked = isAlreadyBooked(slot.id);
              return (
                <div
                  key={slot.id}
                  className={`slot-card ${selectedSlot === slot.id ? 'selected' : ''} ${booked ? 'already-booked' : ''} ${slot.vol ? 'vol' : ''}`}
                  onClick={() => {
                    if (!booked) {
                      setSelectedSlot(selectedSlot === slot.id ? null : slot.id);
                      setError(null);
                    }
                  }}
                >
                  <div className="slot-day">{getDayLabel(slot.datum)}</div>
                  <div className="slot-time">{slot.tijd}</div>
                  <div className="slot-availability">
                    {booked ? (
                      <span className="badge badge-booked">{t.alreadyBooked}</span>
                    ) : slot.vol ? (
                      <span className="badge badge-waitlist">{t.full}</span>
                    ) : (
                      <span className="badge badge-available">
                        {t.spotsAvailable(slot.beschikbaar, slot.maxPlekken)}
                      </span>
                    )}
                  </div>
                  {slot.wachtlijst > 0 && (
                    <div className="slot-waitlist">
                      {t.onWaitlist(slot.wachtlijst)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Registration form */}
        <div className="borstwering-form-section">
          <h2>{t.step2}</h2>

          {success && (
            <div className="borstwering-alert success">{success}</div>
          )}
          {error && (
            <div className="borstwering-alert error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="borstwering-form">
            <div className="form-group">
              <label htmlFor="huisnummer">{t.houseNumber} *</label>
              <input
                type="text"
                id="huisnummer"
                value={form.huisnummer}
                onChange={(e) => setForm({ ...form, huisnummer: e.target.value })}
                placeholder={t.houseNumberPlaceholder}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="naam">{t.name} *</label>
              <input
                type="text"
                id="naam"
                value={form.naam}
                onChange={(e) => setForm({ ...form, naam: e.target.value })}
                placeholder={t.namePlaceholder}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">{t.email} *</label>
              <input
                type="email"
                id="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.emailPlaceholder}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="telefoon">{t.phone}</label>
              <input
                type="tel"
                id="telefoon"
                value={form.telefoon}
                onChange={(e) => setForm({ ...form, telefoon: e.target.value })}
                placeholder={t.phonePlaceholder}
              />
            </div>

            {selectedSlot && (
              <div className="selected-slot-info">
                {t.selected}: <strong>{slots.find(s => s.id === selectedSlot)?.label}</strong>
                {slots.find(s => s.id === selectedSlot)?.vol && (
                  <span className="waitlist-notice"> {t.waitlistLabel}</span>
                )}
              </div>
            )}

            <button
              type="submit"
              className="borstwering-submit"
              disabled={submitting || !selectedSlot}
            >
              {submitting ? t.submitting :
               selectedSlot && slots.find(s => s.id === selectedSlot)?.vol ?
               t.joinWaitlist : t.register}
            </button>
          </form>
        </div>

        {existingBookings.length > 0 && (
          <div className="existing-bookings">
            <h3>{t.yourBookings}</h3>
            {existingBookings.map(b => (
              <div key={b._id} className={`existing-booking ${b.isWachtlijst ? 'waitlist' : ''}`}>
                <span>{slots.find(s => s.id === b.slotId)?.label || b.slotId}</span>
                {b.isWachtlijst && <span className="badge badge-waitlist">{t.waitlist}</span>}
              </div>
            ))}
          </div>
        )}

        <div className="borstwering-footer">
          <p>{t.footer} <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default BorstweringenPlanning;
